'use client'

import { Button } from '@/components/ui/button'
import { Loader2Icon } from 'lucide-react'

interface IChatKitErrorStateProps {
  /**
   * Translation function for localized text
   */
  t: (key: string) => string
  /**
   * Error message to display
   */
  errorMessage: string
}

/**
 * Error state component for when ChatKit script fails to load
 */
export function ChatKitScriptError({
  t,
  errorMessage,
}: IChatKitErrorStateProps) {
  return (
    <div className="flex flex-col items-center justify-center bg-transparent rounded-lg h-[70vh] p-4 sm:p-6">
      <p className="text-red-400 font-semibold mb-2 text-sm sm:text-base text-center">
        Failed to Load ChatKit
      </p>
      <p className="text-gray-400 text-xs sm:text-sm mb-4 max-w-md text-center px-2">
        {errorMessage}
      </p>
      <Button
        variant="outline_2"
        onClick={() => {
          return window.location.reload()
        }}
        className="text-xs sm:text-sm"
      >
        {t('reload_page')}
      </Button>
    </div>
  )
}

interface IChatKitLoadingStateProps {
  /**
   * Translation function for localized text
   */
  t: (key: string) => string
}

/**
 * Loading state component while ChatKit is initializing
 */
export function ChatKitLoadingState({ t }: IChatKitLoadingStateProps) {
  return (
    <div className="flex flex-col items-center justify-center bg-transparent rounded-lg h-[70vh] p-4 sm:p-6">
      <Loader2Icon className="size-10 sm:size-12 text-white mb-3 sm:mb-4 animate-spin" />
      <p className="text-gray-400 mb-2 text-sm sm:text-base text-center">
        {t('initializing')}
      </p>
      <p className="text-gray-500 text-xs sm:text-sm max-w-sm mx-auto text-center px-2 leading-relaxed">
        {t('initializing_description')}
      </p>
      <Button
        variant="outline_2"
        className="my-3 sm:my-4 text-xs sm:text-sm"
        onClick={() => {
          return window.location.reload()
        }}
      >
        {t('reload_page')}
      </Button>
    </div>
  )
}

interface IChatKitInitializationErrorProps {
  /**
   * Translation function for localized text
   */
  t: (key: string) => string
}

/**
 * Error state component for when ChatKit fails to initialize
 */
export function ChatKitInitializationError({
  t,
}: IChatKitInitializationErrorProps) {
  return (
    <div className="flex flex-col items-center justify-center bg-white/5 rounded-lg border border-white/10 h-[calc(100vh-180px)] md:h-[70vh] p-4 sm:p-6">
      <p className="text-red-400 font-semibold mb-2 text-sm sm:text-base text-center">
        {t('chatkit_failed_to_initialize')}
      </p>
      <p className="text-gray-400 text-xs sm:text-sm mb-4 text-center px-2">
        {t('chatkit_failed_to_initialize_description')}
      </p>
      <Button
        onClick={() => {
          return window.location.reload()
        }}
        className="text-xs sm:text-sm"
      >
        {t('reload_page')}
      </Button>
    </div>
  )
}
